// ------------- import external dependencies ---------------
import { AiFillHome, AiOutlineAreaChart, AiFillSetting } from "react-icons/ai";
import { BsFillBarChartFill } from "react-icons/bs";
import { HiUsers, HiOutlineLogout } from "react-icons/hi";

// ------------- import internal dependencies --------------
import { SelectInputType } from "../type";
import _ from "lodash";

export const adminNavOptions = {
  main: [
    {
      label: "Dashboard",
      icon: AiFillHome,
      link: "/user",
    },
    {
      label: "Incident",
      icon: BsFillBarChartFill,
      link: "/user",
    },
    {
      label: "Incidents",
      icon: AiOutlineAreaChart,
      link: "/user",
    },
    {
      label: "Create User",
      icon: HiUsers,
      link: "/user/create",
    },
    {
      label: "Users",
      icon: HiUsers,
      link: "/user",
    },
  ],
  support: [
    {
      label: "Help",
      icon: HiUsers,
    },
    {
      label: "Terms and Conditions",
      icon: HiUsers,
    },
  ],
  base: [
    {
      label: "Settings",
      icon: AiFillSetting,
    },
    {
      label: "Logout",
      icon: HiOutlineLogout,
    },
  ],
};

/**
 * Get changes/differences in values between 2 objs
 * @param obj1
 * @param obj2
 * @returns {}
 */
export const getChangedValues = (obj1: any, obj2: any) => {
  const values: any = {};

  for (const key in obj1) {
    if (!obj2.hasOwnProperty(key) || obj1[key] !== obj2[key])
      values[key] = obj1[key];
  }

  return values;
};

/**
 * Get object value of a string
 * @param arr
 * @param key
 * @param val
 * @returns
 */
export const getObjVal = (
  arr: { [Property in keyof SelectInputType]: string }[],
  key: string,
  val: string
) => {
  if (arr && val !== "") {
    let filterObj = arr.filter((obj: any) => {
      return obj[key] === val;
    });

    return filterObj[0] ?? {};
  }
};

/**
 * Conver Object to array
 * @param obj
 * @returns
 */
export const convertObjEntriesToArray = (obj: {
  [k: string | number]: any;
}) => {
  if (typeof obj === "object") {
    return Object.entries(obj);
  }

  return [];
};

/**
 * Convert to string starting with lower case
 * @param str
 * @returns
 */
export const convertToVariableString = (str: string) => {
  let passedString = str.split(" ").join("");

  return `${passedString.charAt(0).toLocaleLowerCase()}${passedString.slice(
    1
  )}`;
};

/**
 * Handle toast configuration
 * @param toast
 * @returns
 */
export const toastConfig = (toast: any) => ({
  position: toast.POSITION.TOP_RIGHT,
  autoClose: 2000,
  toastId: "1",
});

/**
 * Truncate words with ending ...
 * @param str
 * @ender number number of letters to show
 * @returns
 */
export function truncateWords(str: string, ender: number = 15): string {
  return _.truncate(str, {
    length: ender,
  });
}

export const getDocLink = (attachment: any) => {
  const { data, fileExtension } = attachment;
  const fullStr = prependMimeType(data, fileExtension);
  return fullStr;
};

// ------- input field custom styles -------
export const customStyles = {
  container: (provided: any, state: any) => ({
    ...provided,
    background: "var(--purpleInput)",
    fontFamily: "Inter",
    fontWeight: "400",
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    background: "var(--purpleInput)",
    border: "1px solid rgba(58, 53, 65, 0.23)",
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: "none",
  }),
  // Keeps the menus from being cut off or elements drawn on top of them
  menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
};

export const customStylesFilter = {
  control: (provided: any, state: any) => ({
    ...provided,
    border: "1px solid var(--border_color)",
    height: "50px",
    // maxWidth: "15rem",
    fontSize: ".8rem",
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: "none",
  }),
};

/**
 * remove picked facility from list
 * @param arr
 * @returns
 */
export const filterFacilities = (assignments: any, arr: any) => {
  if (!assignments || !arr) {
    return [];
  }

  const pickedFacilities: string[] = assignments.map(
    (assignmment: any) => assignmment.facility.facilityName
  );

  return arr.filter(
    (facility: any) =>
      pickedFacilities.includes(facility.facilityName) === false
  );
};

/**
 * filter out all non guest user if guest is seleected
 * @param arr
 * @returns
 */
export const filterGuestProfile = (assignments: any, arr: any) => {
  // ---- check if proper value is returned -------
  if (!assignments || !arr) {
    return [];
  }

  if (assignments.length === 1) {
    return arr;
  }

  const guestProfiles: string[] = assignments.map(
    (assignment: any) => assignment.profile.profileName
  );

  if (guestProfiles.includes("Guest User")) {
    return arr.filter((profile: any) => profile.profileName === "Guest User");
  }

  return arr.filter((profile: any) => profile.profileName !== "Guest User");
};

/**
 * Destructure permission for each assignment
 * @param permissionsObj
 */
export const getPermissionObject = (
  permissionsObj: any,
  setPermissionList?: (obj: any) => void
) => {
  let permissionList = convertObjEntriesToArray(permissionsObj);

  let facilityObj: any = {};
  // ------ loop through permissions array -------
  permissionList.forEach((permission: any) => {
    // ------ loop through sub permissions array -------
    permission[1].forEach((obj: any) => {
      // ------- append permission to the facility object ------
      facilityObj[obj.apiName] = obj.permissionValue;
    });
  });

  if (setPermissionList) {
    setPermissionList(facilityObj);
    return;
  }

  return facilityObj;
};

/**
 * Convert file to base64 encoded string
 * @param file
 * @returns
 */
export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Convert files to base64 encoded string
 * @param files
 * @returns
 */
export const convertFilesToBase64 = async (files: [], userId: string) => {
  const base64Files: any = [];

  const docxType =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  for (const file of files as any) {
    const base64String = await convertFileToBase64(file.data);
    base64Files.push({
      name: file.name,
      uploadedBy: userId,
      data:
        file.data.type === docxType
          ? stripMimeTypeDocx(base64String)
          : stripMimeType(base64String),
    });
  }

  return base64Files;
};

export function resetDeleteConfig() {
  return {
    showDelete: false,
    id: "",
    fn: () => {},
  };
}

export function resetViewConfig() {
  return {
    viewFile: false,
    file: "",
    fileExtension: "",
  };
}

export const stripMimeType = (base64String: string): string => {
  const result = base64String.replace(/^data:[a-zA-Z]+\/[a-zA-Z]+;base64,/, "");
  return result;
};

export const stripMimeTypeDocx = (base64String: string): string => {
  const result = base64String.replace(
    /^data:application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document;base64,/,
    ""
  );
  return result;
};

export const prependMimeType = (
  base64Data: string,
  mimeType: string
): string => {
  const fullMimeType = mimeTypeMapping[mimeType.toLowerCase()] || mimeType;
  return `data:${fullMimeType};base64,${base64Data}`;
};

export const mimeTypeMapping: { [key: string]: string } = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

export function formatFileName(str: string) {
  const lastIndex = str.lastIndexOf(".");
  if (lastIndex === -1) return str;
  else return str.substring(0, lastIndex);
}

/**
 * Check for Value or return default value
 * @param value
 * @param defaultValue
 */
export function valueChecker(value: any, defaultValue: any) {
  return value ? value : defaultValue;
}

export function editORUpdate(obj: { edit: boolean }) {
  return obj.edit ? "Update" : "Edit";
}

/**
 * Returns the name value within an object
 * @param {}
 * @returns string
 */
export function getNameFromObj(obj: any) {
  if (_.isEmpty(obj)) return "";
  else return obj.Name || "";
}

/**
 * Converts camelCase to Uppercase separated strings
 * @param string
 * @returns string
 */
export function camelToFlat(string: string) {
  const camelCase = string.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");

  let flat = "";

  camelCase.forEach((word: any) => {
    flat = flat + word.charAt(0).toUpperCase() + word.slice(1) + " ";
  });
  return flat;
}

/**
 * Converts strings with underscores to proper words
 * @param string
 * @returns string
 */
export function underscoreToFlat(string: string): string {
  if (!string) return "";
  // Check if the last character in the string is an underscore
  const lastUnderscoreIndex = string.lastIndexOf("_");
  const hasTrailingUnderscore = lastUnderscoreIndex === string.length - 2;

  // Remove any characters after the last underscore if it's the last character in the string
  if (hasTrailingUnderscore) {
    string = string.slice(0, lastUnderscoreIndex);
  }

  let result = "";
  let capNext = true;
  for (let i = 0; i < string.length; i++) {
    const char = string.charAt(i);
    if (char === "_" || char === " ") {
      result += " ";
      capNext = true;
      continue;
    }
    if (capNext) {
      result += char.toUpperCase();
      capNext = false;
    } else result += char.toLowerCase();
  }

  result = result.trim();

  return result;
}
